import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { HiOutlineMoon, HiOutlineSun, HiArrowRight, HiMenuAlt3, HiX } from 'react-icons/hi';
import {
  HiOutlineWallet,
  HiOutlineBolt,
  HiOutlineChartBar,
  HiOutlineUserPlus,
  HiOutlineLockOpen,
  HiOutlineTrophy,
  HiOutlineBanknotes,
  HiOutlineArrowTrendingUp,
  HiOutlineShieldCheck,
  HiOutlineUsers,
  HiOutlineSquares2X2,
  HiOutlineStar,
  HiOutlineLockClosed,
  HiOutlineBuildingOffice2,
  HiOutlineRocketLaunch,
  HiOutlineGlobeAlt,
  HiOutlineCube,
  HiOutlineArrowsPointingOut,
  HiOutlineFlag,
  HiOutlineQuestionMarkCircle,
  HiOutlineMap,
  HiOutlineQueueList,
  HiOutlineCircleStack,
  HiOutlineSparkles,
  HiOutlineCheckCircle,
  HiOutlineClipboard,
  HiOutlineArrowTopRightOnSquare,
} from 'react-icons/hi2';
import { useTheme } from '../context/ThemeContext';
import { useWallet } from '../context/WalletContext';
import { api } from '../services/api';
import { readTokenDetails } from '../services/wallet';
import { PublicSiteSkeleton } from '../components/Skeleton';
import { RANK_TITLES, rankCode, rankTitle } from '../utils/ranks';
import './PublicSite.css';

const NAV = [
  ['#how', 'How it works', HiOutlineQueueList],
  ['#earnings', 'Earnings', HiOutlineChartBar],
  ['#levels', 'Levels', HiOutlineSquares2X2],
  ['#ranks', 'Ranks', HiOutlineTrophy],
  ['#roadmap', 'Roadmap', HiOutlineMap],
  ['#faq', 'FAQ', HiOutlineQuestionMarkCircle],
];

const RANKS = Object.keys(RANK_TITLES).map(Number);

const LEVEL_FALLBACK = [
  [1, '10', '0', '0', '0', '0'],
  [2, '5', '2', '100', '0', '0'],
  [3, '3', '3', '100', '0', '0'],
  [4, '2', '4', '100', '0', '0'],
  [5, '2', '5', '100', '0', '0'],
  [6, '1', '5', '200', '15', '200'],
  [7, '1', '5', '200', '15', '200'],
  [8, '1', '5', '200', '15', '200'],
  [9, '1', '5', '200', '15', '200'],
  [10, '1', '5', '200', '15', '200'],
  [11, '0.5', '5', '400', '25', '400'],
  [12, '0.5', '5', '400', '25', '400'],
  [13, '0.5', '5', '400', '25', '400'],
  [14, '0.5', '5', '400', '25', '400'],
  [15, '0.5', '5', '400', '25', '400'],
];

const HOW = [
  { title: 'Connect', text: 'Open the app with MetaMask, Trust Wallet, or another injected wallet on BNB Smart Chain. New wallets register with a referrer.', Icon: HiOutlineWallet },
  { title: 'Activate', text: 'Choose a USDT plan inside the published minimum and maximum. The payment is confirmed on-chain and added to your invested balance.', Icon: HiOutlineBolt },
  { title: 'Accrue', text: 'Eligible balances receive the daily rate. Lifetime income from returns and commissions stops at the earn cap.', Icon: HiOutlineChartBar },
  { title: 'Invite', text: 'A direct referral purchase pays your direct commission. Deeper ROI follows the 15-level chart once those levels are unlocked.', Icon: HiOutlineUserPlus },
  { title: 'Unlock levels', text: 'Level 1 is open. Higher levels open only after the direct and second-level deposit counts for that level are met.', Icon: HiOutlineLockOpen },
  { title: 'Match a rank', text: 'Rank uses the smaller of your strongest direct leg and the rest of your direct legs. Ranks 7–10 also need ranked users on three direct teams.', Icon: HiOutlineTrophy },
];

const PHASE_ICONS = [
  HiOutlineBuildingOffice2,
  HiOutlineRocketLaunch,
  HiOutlineUsers,
  HiOutlineCube,
  HiOutlineArrowsPointingOut,
  HiOutlineGlobeAlt,
];

const viewport = { once: true, amount: 0.2 };

function LaunchCountdown({ launchAt }) {
  const target = Date.parse(String(launchAt || ''));
  const [now, setNow] = useState(() => Date.now());
  const valid = Number.isFinite(target);

  useEffect(() => {
    if (!valid || target <= Date.now()) return undefined;
    const id = window.setInterval(() => {
      const next = Date.now();
      setNow(next);
      if (next >= target) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [target, valid]);

  if (!valid || target <= now) return null;

  const remain = target - now;
  const parts = [
    ['Days', Math.floor(remain / 86400000)],
    ['Hours', Math.floor((remain % 86400000) / 3600000)],
    ['Minutes', Math.floor((remain % 3600000) / 60000)],
    ['Seconds', Math.floor((remain % 60000) / 1000)],
  ];

  return (
    <div className="public__launch" role="timer" aria-label="Project launch countdown">
      {parts.map(([label, value]) => (
        <div key={label} className="public__launch-unit">
          <strong>{String(value).padStart(2, '0')}</strong>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

function Rise({ as = 'div', className, children, delay = 0 }) {
  const reduce = useReducedMotion();
  if (reduce) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewport}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </Tag>
  );
}

function Stagger({ as = 'div', className, children }) {
  const reduce = useReducedMotion();
  if (reduce) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: 0.07 } },
      }}
    >
      {children}
    </Tag>
  );
}

function Item({ as = 'div', className, children }) {
  const reduce = useReducedMotion();
  if (reduce) {
    const Tag = as;
    return <Tag className={className}>{children}</Tag>;
  }
  const Tag = motion[as];
  return (
    <Tag
      className={className}
      variants={{
        hidden: { opacity: 0, y: 22 },
        show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
      }}
    >
      {children}
    </Tag>
  );
}

const PHASES = [
  {
    n: '01',
    name: 'Foundation',
    title: 'Building the Core of Dynamic Future Defi',
    body: 'The first phase focuses on establishing the technological, operational and brand foundation of the Dynamic Future Defi ecosystem. During this stage, the project moves from concept to structured development, creating the infrastructure required for future expansion.',
    listLabel: 'Key Objectives',
    items: [
      'Dynamic Future Defi concept and ecosystem planning',
      'Brand identity development',
      'DFI Token architecture',
      'Smart contract development',
      'Tokenomics planning',
      'Website development',
      'Project documentation',
      'Community platform setup',
      'Social media presence',
      'Security and contract review',
      'Initial ecosystem infrastructure',
      'Legal and compliance review where applicable',
    ],
    goal: 'Create a transparent, secure and scalable foundation for the Dynamic Future Defi ecosystem.',
  },
  {
    n: '02',
    name: 'DFI Launch',
    title: 'Bringing DFI to the Market',
    body: 'The second phase introduces DFI Token as the core digital asset of the Dynamic Future Defi ecosystem. The focus is on deploying the token, establishing initial liquidity, enabling decentralized trading and providing the community with official channels to verify token information.',
    listLabel: 'Key Objectives',
    items: [
      'DFI Token deployment',
      'Smart contract verification',
      'Official contract address publication',
      'Initial liquidity setup',
      'PancakeSwap trading activation',
      'DFI trading pair establishment',
      'Official website launch',
      'Dynamic Future Defi community onboarding',
      'Wallet and token information integration',
      'Referral program activation, subject to final rules',
      'Public token and liquidity information',
    ],
    goal: 'Establish DFI as the foundational digital asset of the Dynamic Future Defi ecosystem and create an accessible entry point for the community.',
  },
  {
    n: '03',
    name: 'Community Expansion',
    title: 'Building a Global Dynamic Future Defi Community',
    body: 'A strong ecosystem requires an active and engaged community. This phase expands Dynamic Future Defi across different markets and develops structured programs that recognize participation, contribution and leadership.',
    listLabel: 'Key Objectives',
    items: [
      'Global community expansion',
      'Regional Dynamic Future Defi communities',
      'Community education initiatives',
      'Referral ecosystem development',
      '15-Level Community Program',
      '10-Rank Leadership Program',
      'Leadership recognition',
      'Community milestones',
      'Awards & Rewards Program',
      'Ambassador initiatives',
      'Community events and campaigns',
      'Multilingual community support',
    ],
    goal: 'Develop a strong global network of participants, contributors and community leaders around DFI.',
  },
  {
    n: '04',
    name: 'Ecosystem Development',
    title: 'Expanding the Utility of DFI',
    body: 'As the community grows, Dynamic Future Defi intends to focus increasingly on expanding the practical role and utility of DFI within the ecosystem. This phase moves Dynamic Future Defi beyond its initial token and community infrastructure toward a broader Web3 environment.',
    listLabel: 'Planned Development Areas',
    items: [
      'Expanded DFI utility',
      'Holding-program development',
      'DeFi integrations',
      'Ecosystem dashboard',
      'Community account features',
      'Portfolio and reward tracking',
      'Blockchain analytics integration',
      'Additional Web3 applications',
      'Strategic ecosystem partnerships',
      'Third-party integrations',
      'Community-focused digital products',
      'Improved ecosystem automation',
      'Mobile-friendly ecosystem access',
    ],
    goal: 'Increase the practical utility of DFI and develop an interconnected ecosystem around the token.',
  },
  {
    n: '05',
    name: 'Ecosystem Expansion',
    title: 'From a Token to a Broader Web3 Ecosystem',
    body: 'Phase 5 is Dynamic Future Defi’s transition toward a broader decentralized digital ecosystem. The project may explore additional products, integrations and blockchain capabilities based on technology, community demand, regulatory considerations and ecosystem sustainability.',
    listLabel: 'Potential Development Areas',
    items: [
      'Advanced DeFi utilities',
      'DFI-powered Web3 applications',
      'Additional decentralized services',
      'Community governance features',
      'Broader partner integrations',
      'Expanded DFI use cases',
      'Cross-platform integrations',
      'Additional blockchain interoperability',
      'Ecosystem marketplace opportunities',
      'Developer and partner infrastructure',
    ],
    goal: 'Transform Dynamic Future Defi from a token-centered ecosystem into a broader Web3 platform with multiple potential utilities.',
  },
  {
    n: '06',
    name: 'Global Adoption',
    title: 'Expanding the Dynamic Future Defi Network',
    body: 'The long-term strategy of Dynamic Future Defi is to develop an ecosystem capable of supporting users and communities across multiple regions. This phase focuses on broader accessibility, partnerships, ecosystem adoption and infrastructure capable of supporting continued international growth.',
    listLabel: 'Long-Term Objectives',
    items: [
      'International community development',
      'Global strategic partnerships',
      'Wider DFI ecosystem adoption',
      'Expanded Web3 integrations',
      'Regional ecosystem initiatives',
      'Developer collaborations',
      'Additional utility partnerships',
      'Improved scalability and accessibility',
      'Continued security development',
      'Long-term ecosystem sustainability',
    ],
    goal: 'Build a globally accessible Dynamic Future Defi ecosystem supported by technology, utility and community.',
  },
];

const FAQ = [
  ['What do I deposit?', 'USDT on BNB Smart Chain. The plan must sit between the published minimum and maximum. Preset amounts are shortcuts, not a different product.'],
  ['How is daily income calculated?', 'It is the daily rate applied to your confirmed invested balance. Credits stop when lifetime earnings reach the earn-cap multiple of what you invested.'],
  ['Who receives downline commission?', 'Only an upline who has unlocked that depth, and only if they have invested at least the minimum self-invest amount. Otherwise that commission is skipped.'],
  ['What is a level?', 'Depth in your referral tree. Level 1 is someone you invited. Level 2 is someone they invited. Commission stops at level 15.'],
  ['What is matching business?', 'Each direct invite is a leg. The largest leg is the strong team. Every other direct leg is added together. Your qualifying amount is the smaller of those two.'],
  ['Why would a rank bonus stay locked?', 'The weekly amount is visible, but it is not added to your balance unless your matching business grew by the required percent over the required number of days. Unpaid weeks stay in a locked balance and are released together on the next week that qualifies.'],
  ['How does the referral pool work?', 'Referrers are ranked by the total deposited by their direct users. At the end of each day a percent of that day’s confirmed deposits is split by place. If fewer than five qualify, the unused percents are not given to the others.'],
];

function scrollToSection(event) {
  const href = event.currentTarget.getAttribute('href');
  if (!href || href.charAt(0) !== '#') return;
  const target = document.getElementById(href.slice(1));
  if (!target) return;
  event.preventDefault();
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  window.history.replaceState(null, '', href);
}

function num(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function chainLabel(chainId) {
  const id = Number(chainId);
  if (id === 56) return 'BNB Smart Chain';
  if (id === 97) return 'BSC Testnet';
  return chainId ? `Chain ${chainId}` : '—';
}

function explorerTokenUrl(chainId, address) {
  const host = Number(chainId) === 97 ? 'https://testnet.bscscan.com' : 'https://bscscan.com';
  return `${host}/token/${address}`;
}

function formatSupply(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function mergeLevelRanges(rows) {
  const sorted = [...rows].sort((a, b) => Number(a.level) - Number(b.level));
  const groups = [];
  sorted.forEach((row) => {
    const level = Number(row.level);
    const last = groups[groups.length - 1];
    const sameRates = last
      && Number(last.percent) === Number(row.percent)
      && Number(last.boosted) === Number(row.boosted);
    if (last && last.to + 1 === level && sameRates) {
      last.to = level;
      return;
    }
    groups.push({
      from: level,
      to: level,
      percent: row.percent,
      boosted: row.boosted,
    });
  });
  return groups;
}

function levelRangeLabel(group) {
  if (group.from === group.to) return `Level ${group.from}`;
  return `Levels ${group.from}–${group.to}`;
}

function TokenDetails({ contract, chainId }) {
  const [meta, setMeta] = useState(null);
  const [failed, setFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!contract) return undefined;
    let cancelled = false;
    setMeta(null);
    setFailed(false);
    readTokenDetails(contract, chainId)
      .then((data) => {
        if (!cancelled) setMeta(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [contract, chainId]);

  if (!contract) return null;

  async function copyContract() {
    try {
      await navigator.clipboard.writeText(contract);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  const pending = !meta && !failed;
  const network = chainLabel(chainId);
  const stats = [
    { label: 'Symbol', value: meta?.symbol || (pending ? '…' : '—'), Icon: HiOutlineSparkles },
    { label: 'Decimals', value: meta ? String(meta.decimals) : (pending ? '…' : '—'), Icon: HiOutlineCircleStack },
    { label: 'Total supply', value: meta ? formatSupply(meta.totalSupply) : (pending ? '…' : '—'), Icon: HiOutlineBanknotes },
    { label: 'Network', value: network, hint: chainId ? `Chain ${chainId}` : '', Icon: HiOutlineGlobeAlt },
  ];

  return (
    <section className="public__token" aria-label="Token details">
      <div className="public__token-card">
        <div className="public__token-identity">
          <img src="/logo-256.png" alt="" />
          <div>
            <p className="public__kicker">Official token</p>
            <h2>{meta?.name || (pending ? 'Reading contract…' : 'Token')}</h2>
            <p className="public__token-symbol">{meta?.symbol || (pending ? '…' : 'DFI')}</p>
            <div className="public__token-chips">
              <span>{network}</span>
              <span>BEP-20</span>
            </div>
          </div>
        </div>
        <ul className="public__token-stats">
          {stats.map(({ label, value, hint, Icon }) => (
            <li key={label}>
              <Icon aria-hidden size={18} />
              <span>{label}</span>
              <strong className={String(value).length > 12 ? 'is-long' : ''}>{value}</strong>
              {hint ? <em>{hint}</em> : null}
            </li>
          ))}
        </ul>
        <div className="public__token-address">
          <span>Contract</span>
          <code>{contract}</code>
          <div className="public__token-actions">
            <button type="button" onClick={copyContract}>
              <HiOutlineClipboard aria-hidden size={15} />
              {copied ? 'Copied' : 'Copy'}
            </button>
            <a href={explorerTokenUrl(chainId, contract)} target="_blank" rel="noreferrer">
              <HiOutlineArrowTopRightOnSquare aria-hidden size={15} />
              BscScan
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Section({ id, kicker, title, lede, children }) {
  return (
    <section className="public__section" id={id}>
      <Rise className="public__section-head">
        <p className="public__kicker">{kicker}</p>
        <h2>{title}</h2>
        {lede ? <p>{lede}</p> : null}
      </Rise>
      {children}
    </section>
  );
}

export default function PublicSite() {
  const { config, loading } = useWallet();
  const { isDark, toggleTheme } = useTheme();
  const [charts, setCharts] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function onKey(event) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    function onResize() {
      if (window.innerWidth > 980) setMenuOpen(false);
    }
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  useEffect(() => {
    let cancelled = false;
    api.commissions()
      .then((data) => {
        if (!cancelled) setCharts(data.charts || null);
      })
      .catch(() => { });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <PublicSiteSkeleton />;

  const cfg = config || {};
  const minPlan = cfg.plan_min_usdt || '50';
  const maxPlan = cfg.plan_max_usdt || '10000';
  const daily = cfg.daily_return_percent || '1';
  const cap = cfg.earn_cap_multiplier || '2';
  const capHigh = cfg.earn_cap_high_multiplier || '3';
  const capFrom = cfg.earn_cap_high_from_rank || '7';
  const direct = cfg.direct_upline_commission_percent || '5';
  const selfMin = cfg.commission_min_invest_usdt || '100';
  const growthPct = cfg.rank_bonus_growth_percent || '15';
  const growthDays = cfg.rank_bonus_growth_days || '30';
  const poolPct = cfg.referral_top_pool_percent || '2';
  const roi = charts?.roi_downline;
  const levels = roi?.levels?.length
    ? roi.levels
    : LEVEL_FALLBACK.map(([level, percent, gen1, gen1Min, gen2, gen2Min]) => ({
      level,
      percent,
      boosted: Number(percent) + num(roi?.extra_percent, 5),
      gen1_count: Number(gen1),
      gen1_min: Number(gen1Min),
      gen2_count: Number(gen2),
      gen2_min: Number(gen2Min),
    }));
  const extraRank = roi?.extra_min_rank ?? 7;
  const extraPct = roi?.extra_percent ?? 5;
  const poolPlaces = [1, 2, 3, 4, 5].map((place) => ({
    place,
    percent: cfg[`referral_top_${place}_percent`] || [40, 30, 20, 5, 5][place - 1],
  }));

  return (
    <div className="public">
      <header className="public__nav">
        <button
          className="public__menu-btn"
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <HiX size={22} /> : <HiMenuAlt3 size={22} />}
        </button>
        <Link to="/" className="public__brand" onClick={() => setMenuOpen(false)}>
          <img className="public__logo" src="/logo-64.png" alt="" />
          <strong>
            <span className="public__brand-full">Dynamic Future Defi</span>
            <span className="public__brand-short">DFI Defi</span>
          </strong>
        </Link>
        <nav className="public__links" aria-label="Page sections">
          {NAV.map(([href, label, Icon]) => (
            <a key={href} href={href} onClick={scrollToSection}><Icon aria-hidden size={15} />{label}</a>
          ))}
        </nav>
        <div className="public__nav-actions">
          <button
            className="theme-toggle"
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            title={isDark ? 'Light mode' : 'Dark mode'}
          >
            {isDark ? <HiOutlineSun size={18} /> : <HiOutlineMoon size={18} />}
          </button>
          <Link className="btn btn--primary" to="/dashboard">
            Start
            <HiArrowRight />
          </Link>
        </div>
      </header>

      <button
        className={`public__backdrop${menuOpen ? ' is-open' : ''}`}
        type="button"
        aria-label="Close menu"
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />
      <aside className={`public__drawer${menuOpen ? ' is-open' : ''}`} inert={menuOpen ? undefined : ''}>
        <div className="public__drawer-head">
          <strong>Menu</strong>
          <button type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)}>
            <HiX size={20} />
          </button>
        </div>
        <nav aria-label="Page sections">
          {NAV.map(([href, label, Icon]) => (
            <a
              key={href}
              href={href}
              onClick={(event) => {
                setMenuOpen(false);
                scrollToSection(event);
              }}
            >
              <Icon aria-hidden size={18} />
              {label}
            </a>
          ))}
        </nav>
      </aside>

      <section className="public__hero">
        <div className="public__hero-bg" aria-hidden />
        <motion.div
          className="public__hero-copy"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="public__hero-pill">Powered by Binance Smart Chain (BSC)</p>
          <h1 className="public__brand-hero">
            The future of <span className="public__hero-word">finance</span>
            <span className="public__hero-accent">Solved.</span>
          </h1>
          <p className="public__tagline">
            <span className="public__hero-shine">Dynamic Future DeFi</span> is a next-generation ecosystem built for transparency, security, and long-term value in decentralized finance.
          </p>
          <LaunchCountdown launchAt={cfg.launch_at} />
          <div className="public__cta">
            <Link className="btn btn--primary btn--lg" to="/dashboard">
              Start Earning Now
              <HiArrowRight />
            </Link>
          </div>
        </motion.div>
      </section>

      <TokenDetails contract={cfg.dfi_contract} chainId={cfg.chain_id || '56'} />

      <Section
        id="how"
        kicker="How it works"
        title="From wallet connect to claim"
        lede="Every step stays in your account. Deposits are USDT. Claims are DFI. Commission only moves when the receiver has unlocked that level and invested enough themselves."
      >
        <Stagger as="ol" className="public__steps">
          {HOW.map(({ title, text, Icon }) => (
            <Item as="li" key={title}>
              <span className="public__mark"><Icon aria-hidden /></span>
              <strong>{title}</strong>
              <p>{text}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <section className="public__section" aria-label="Program figures">
        <Stagger className="public__facts">
          {[
            [HiOutlineBanknotes, 'Plan range', `${minPlan}–${maxPlan} USDT`],
            [HiOutlineArrowTrendingUp, 'Daily rate', `${daily}%`],
            [HiOutlineShieldCheck, 'Earn cap', `${cap}× / ${capHigh}× from rank ${capFrom}`],
            [HiOutlineUserPlus, 'Direct commission', `${direct}%`],
            [HiOutlineSquares2X2, 'Commission depth', '15 levels'],
            [HiOutlineTrophy, 'Ranks', '1–10'],
          ].map(([Icon, label, value]) => (
            <Item key={label} className="public__fact">
              <span className="public__mark"><Icon aria-hidden /></span>
              <span>{label}</span>
              <strong>{value}</strong>
            </Item>
          ))}
        </Stagger>
      </section>

      <Section
        id="earnings"
        kicker="Earning criteria"
        title="Five ways the account can credit you"
        lede={`You only receive downline commission after investing at least ${selfMin} USDT. Personal daily ROI still follows your own deposit and the earn cap.`}
      >
        <Stagger className="public__cards">
          <Item as="article">
            <span className="public__mark"><HiOutlineChartBar aria-hidden /></span>
            <h3>1. Daily ROI</h3>
            <p>Your own plan. {daily}% of invested USDT, until the {cap}× cap is full.</p>
          </Item>
          <Item as="article">
            <span className="public__mark"><HiOutlineUserPlus aria-hidden /></span>
            <h3>2. Direct purchase</h3>
            <p>{direct}% of a direct referral’s confirmed plan is credited to you, if you meet the {selfMin} USDT self-invest rule.</p>
          </Item>
          <Item as="article">
            <span className="public__mark"><HiOutlineSquares2X2 aria-hidden /></span>
            <h3>3. ROI downline</h3>
            <p>When someone below you receives daily ROI, up to 15 uplines can receive a percent of that credit. {rankTitle(extraRank)}+ adds {extraPct}% on the open levels.</p>
          </Item>
          <Item as="article">
            <span className="public__mark"><HiOutlineTrophy aria-hidden /></span>
            <h3>4. Rank bonus</h3>
            <p>Each unlocked rank has a weekly amount and a max. It is paid only when matching business grew at least {growthPct}% over {growthDays} days. Otherwise that week is locked.</p>
          </Item>
          <Item as="article">
            <span className="public__mark"><HiOutlineStar aria-hidden /></span>
            <h3>5. Referral pool</h3>
            <p>{poolPct}% of each day’s confirmed deposits is paid at day-end to the top five referrers by direct deposit volume, using the place percents below.</p>
          </Item>
          <Item as="article">
            <span className="public__mark"><HiOutlineLockClosed aria-hidden /></span>
            <h3>Locked release</h3>
            <p>Weeks that miss the growth rule stay visible as a locked balance. The next qualifying week pays that locked balance and the new week together.</p>
          </Item>
        </Stagger>
        <Stagger className="public__pool">
          {poolPlaces.map((slot) => (
            <Item key={slot.place}>
              <HiOutlineTrophy aria-hidden />
              <span>Place {slot.place}</span>
              <strong>{slot.percent}%</strong>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section
        id="levels"
        kicker="Levels"
        title="Fifteen levels of ROI commission"
        lede={`Each open level pays a percent of that downline member’s daily ROI. ${rankTitle(extraRank)} and above receive the higher rate.`}
      >
        <Stagger className="public__levels">
          {mergeLevelRanges(levels).map((group) => (
            <Item key={`${group.from}-${group.to}`} className="public__level">
              <span className="public__level-index">{levelRangeLabel(group)}</span>
              <div className="public__level-rates">
                <div>
                  <em>Base</em>
                  <strong>{group.percent}%</strong>
                </div>
                <div>
                  <em>{rankTitle(extraRank)}+</em>
                  <strong>{group.boosted}%</strong>
                </div>
              </div>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section
        id="ranks"
        kicker="Ranks"
        title="Explorer through DynaFi Legend"
        lede="These are the ranks you can reach. Your rank follows the smaller of your strongest direct leg and the rest of your legs, and it updates on the daily refresh."
      >
        <Stagger as="ol" className="public__ranks">
          {RANKS.map((rank) => (
            <Item as="li" key={rank} className={`public__rank${rank === 10 ? ' is-top' : ''}`}>
              <span className="public__rank-n" aria-hidden>{rankCode(rank)}</span>
              <strong>{RANK_TITLES[rank]}</strong>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section
        id="roadmap"
        kicker="Dynamic Future Defi Roadmap"
        title="Building the Dynamic Future, Step by Step"
      >
        <Rise className="public__roadmap-intro">
          <p>Dynamic Future Defi is being developed with a long-term vision to build a community-driven Web3 and DeFi ecosystem around the DFI Token.</p>
          <p>Our roadmap represents the planned evolution of Dynamic Future Defi—from establishing the project’s foundation and launching DFI to expanding the global community, developing additional utilities, and building a broader decentralized ecosystem.</p>
          <p>Each phase is designed to strengthen the foundation for the next stage of Dynamic Future Defi’s journey.</p>
        </Rise>
        <ol className="public__phases">
          {PHASES.map((phase, index) => {
            const Icon = PHASE_ICONS[index];
            return (
              <Rise as="li" key={phase.n} className="public__phase">
                <div className="public__phase-index" aria-hidden>{phase.n}</div>
                <article className="public__phase-card">
                  <div className="public__phase-title">
                    <span className="public__mark"><Icon aria-hidden /></span>
                    <div>
                      <p className="public__kicker">Phase {phase.n} — {phase.name}</p>
                      <h3>{phase.title}</h3>
                    </div>
                  </div>
                  <p>{phase.body}</p>
                  <p className="public__phase-label">{phase.listLabel}</p>
                  <ul className="public__phase-items">
                    {phase.items.map((item) => (
                      <li key={item}><HiOutlineCheckCircle aria-hidden />{item}</li>
                    ))}
                  </ul>
                  <div className="public__phase-goal">
                    <span>Phase goal</span>
                    <p>{phase.goal}</p>
                  </div>
                </article>
              </Rise>
            );
          })}
        </ol>
        <Rise className="public__future">
          <p className="public__kicker">The Dynamic Future</p>
          <h3>The Journey Continues</h3>
          <p>Dynamic Future Defi’s roadmap does not end with a single launch.</p>
          <p>Our long-term objective is to continuously explore how DFI Token, blockchain technology, decentralized finance and community participation can work together within an evolving digital ecosystem.</p>
          <div className="public__future-line">
            <span><HiOutlineCircleStack aria-hidden />The journey begins with a token.</span>
            <span><HiOutlineUsers aria-hidden />It grows through community.</span>
            <span><HiOutlineSparkles aria-hidden />It expands through utility.</span>
            <span><HiOutlineFlag aria-hidden />And it moves toward a Dynamic Future.</span>
          </div>
          <p className="public__motto">DFI — One Token. One Ecosystem. One Community. A Dynamic Future.</p>
          <p className="public__motto public__motto--soft">Trade. Hold. Build. Grow.</p>
        </Rise>
      </Section>

      <Section
        id="faq"
        kicker="FAQ"
        title="Before you connect"
      >
        <Stagger className="public__faq">
          {FAQ.map(([q, a]) => (
            <Item as="article" key={q}>
              <h3><HiOutlineQuestionMarkCircle aria-hidden />{q}</h3>
              <p>{a}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Rise as="section" className="public__close">
        <img className="public__close-logo" src="/logo-256.png" alt="" />
        <p className="public__kicker">DFI</p>
        <h2>Dynamic Future Defi</h2>
        <p className="public__close-motto">One token. One ecosystem. One community.</p>
        <p>Connect a wallet, activate a plan between {minPlan} and {maxPlan} USDT, and track level, rank, and income from the app.</p>
        <Link className="btn btn--primary btn--lg" to="/dashboard">
          Start Earning
          <HiArrowRight />
        </Link>
      </Rise>

      <footer className="public__foot">
        <span className="public__foot-brand">
          <img src="/logo-64.png" alt="Dynamic Future Defi" />
          <span>© {new Date().getFullYear()} Dynamic Future Defi</span>
        </span>
        <address className="public__foot-address">
          <HiOutlineBuildingOffice2 aria-hidden />
          <span>
            Kuala Lumpur City Centre
            <br />
            Kuala Lumpur, Federal Territory of Kuala Lumpur 50088
            <br />
            Malaysia
          </span>
        </address>
      </footer>
    </div>
  );
}
